<template>
  <v-container grid-list-xs>
    <v-card>
      <v-card-text v-if="!canViewStore">Selecione um Grupo</v-card-text>
      <template v-else>
        <v-toolbar density="comfortable" color="transparent" class="px-2">
          <v-btn icon variant="text" @click="goBack" aria-label="Voltar">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-toolbar-title class="d-flex align-center flex-wrap ga-2">
            <span class="text-h6">{{ store?.name || `Loja #${$route.params.id}` }}</span>
            <v-chip v-if="store?.group?.name" size="small" variant="outlined" class="ml-2">
              {{ store.group.name }}
            </v-chip>
            <v-chip v-if="store?.type" size="small" variant="tonal" class="ml-2">
              {{ typeLabel(store.type) }}
            </v-chip>
            <v-chip
              v-if="store?.status"
              size="small"
              variant="tonal"
              class="ml-1"
              :color="store.status === 'active' ? 'success' : 'default'"
            >
              {{ statusLabel(store.status) }}
            </v-chip>
            <v-chip
              v-if="store?.pix_enabled"
              size="small"
              color="primary"
              variant="tonal"
              class="ml-1"
            >
              Pix
            </v-chip>
            <v-chip
              v-if="store?.tap_enabled"
              size="small"
              color="secondary"
              variant="tonal"
              class="ml-1"
            >
              TAP
            </v-chip>
            <v-chip
              v-if="store?.invoice_enabled"
              size="small"
              color="info"
              variant="tonal"
              class="ml-1"
            >
              Cobrança
            </v-chip>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="canMutate"
            color="primary"
            variant="tonal"
            :disabled="!store?.id || loadingStore"
            @click="startEditStore"
          >
            <v-icon class="mr-1">mdi-pencil</v-icon>
            Editar
          </v-btn>
        </v-toolbar>

        <v-alert
          v-if="isPlatformAdmin && !canMutate"
          type="info"
          variant="tonal"
          density="compact"
          class="ma-3"
        >
          Visualização somente leitura. Selecione o grupo desta loja para gerenciar produtos,
          estoque, vendedores e editar.
        </v-alert>

        <v-progress-linear v-if="loadingStore" indeterminate color="primary"></v-progress-linear>

        <template v-if="store?.id">
          <v-tabs v-model="tab" color="primary" class="px-2">
            <v-tab value="products">Produtos</v-tab>
            <v-tab value="stock">Estoque</v-tab>
            <v-tab value="sellers">Vendedores</v-tab>
            <v-tab value="sales">Vendas</v-tab>
          </v-tabs>

          <v-window v-model="tab">
            <v-window-item value="products">
              <v-card flat>
                <v-card-title class="d-flex align-center flex-wrap ga-2">
                  <span>Produtos da loja</span>
                  <v-spacer></v-spacer>
                  <DialogAttachStoreProduct
                    v-if="canMutate"
                    :user="user"
                    :storeId="store.id"
                    v-model="editStoreProductModel"
                    :floatRight="true"
                    :excludeProductIds="attachedProductIds"
                    @created="onStoreProductSaved"
                    @updated="onStoreProductSaved"
                  />
                </v-card-title>
                <v-card-text>
                  <v-alert
                    v-if="!canMutate"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                  >
                    Selecione um grupo para listar e gerenciar produtos desta loja.
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="productHeaders"
                    :items="storeProducts"
                    :loading="loadingProducts"
                    :items-per-page="-1"
                    return-object
                    class="elevation-1"
                    hide-default-footer
                  >
                    <template v-slot:item.product_name="{ item }">
                      <span>{{ productName(item) }}</span>
                    </template>
                    <template v-slot:item.base_price="{ item }">
                      <span>{{ formatPrice(item?.product?.base_price) }}</span>
                    </template>
                    <template v-slot:item.price_override="{ item }">
                      <span>{{
                        item?.price_override == null ? '—' : formatPrice(item.price_override)
                      }}</span>
                    </template>
                    <template v-slot:item.effective_price="{ item }">
                      <span>{{ formatPrice(effectivePrice(item)) }}</span>
                    </template>
                    <template v-slot:item.active="{ value }">
                      <span>{{ value ? 'Sim' : 'Não' }}</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-menu>
                        <template v-slot:activator="{ props }">
                          <v-btn icon variant="text" v-bind="props">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="editStoreProductModel = { ...item }">
                            <v-list-item-title>
                              <v-icon color="primary" class="mr-2">mdi-pencil</v-icon>
                              Editar
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-window-item>

            <v-window-item value="stock">
              <v-card flat>
                <v-card-title class="d-flex align-center flex-wrap ga-2">
                  <span>Saldos (claim da loja)</span>
                  <v-spacer></v-spacer>
                  <DialogStockMove
                    v-if="canMutate"
                    :user="user"
                    :storeId="store.id"
                    v-model="stockMoveModel"
                    :productOptions="stockProductOptions"
                    :floatRight="true"
                    @moved="onStockMoved"
                  />
                </v-card-title>
                <v-card-text>
                  <v-alert
                    v-if="!canMutate"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                  >
                    Selecione um grupo para listar e alocar estoque.
                  </v-alert>
                  <v-alert
                    v-else
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                  >
                    Quantidade = claim da loja. PDV usa o mínimo entre claim e disponível no grupo.
                    Compras físicas: menu <strong>Estoque do grupo</strong>.
                  </v-alert>
                  <template v-if="canMutate">
                  <v-data-table
                    :headers="balanceHeaders"
                    :items="balances"
                    :loading="loadingBalances"
                    :items-per-page="-1"
                    :row-props="balanceRowProps"
                    return-object
                    class="elevation-1"
                    hide-default-footer
                  >
                    <template v-slot:item.product_name="{ item }">
                      <span>{{ balanceProductName(item) }}</span>
                    </template>
                    <template v-slot:item.variant_label="{ item }">
                      <span>{{ variantLabel(item) }}</span>
                    </template>
                    <template v-slot:item.quantity="{ value }">
                      <span>{{ formatQty(value) }}</span>
                    </template>
                    <template v-slot:item.store_available="{ value }">
                      <span>{{ formatQty(value) }}</span>
                    </template>
                    <template v-slot:item.group_on_hand="{ value }">
                      <span>{{ formatQty(value) }}</span>
                    </template>
                    <template v-slot:item.group_available="{ value }">
                      <span>{{ formatQty(value) }}</span>
                    </template>
                    <template v-slot:item.pdv_available="{ value }">
                      <span>{{ formatQty(value) }}</span>
                    </template>
                    <template v-slot:item.min_quantity="{ value }">
                      <span>{{ value == null ? '—' : formatQty(value) }}</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn
                        icon
                        variant="text"
                        density="compact"
                        aria-label="Alocar / desalocar"
                        @click="startStockMove(item)"
                      >
                        <v-icon color="primary">mdi-store-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </template>
                </v-card-text>

                <template v-if="canMutate">
                <v-card-title class="d-flex align-center flex-wrap ga-2 pt-4">
                  <span>Movimentações</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    variant="text"
                    density="compact"
                    :loading="loadingMovements"
                    aria-label="Atualizar movimentações"
                    @click="loadMovements"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    :headers="movementHeaders"
                    :items="movements"
                    :loading="loadingMovements"
                    :items-per-page="50"
                    return-object
                    class="elevation-1"
                    hide-default-footer
                  >
                    <template v-slot:item.type="{ value }">
                      <span>{{ movementTypeLabel(value) }}</span>
                    </template>
                    <template v-slot:item.product_name="{ item }">
                      <span>{{ movementProductName(item) }}</span>
                    </template>
                    <template v-slot:item.variant_label="{ item }">
                      <span>{{ variantLabel(item) }}</span>
                    </template>
                    <template v-slot:item.quantity="{ value }">
                      <span>{{ formatQty(value) }}</span>
                    </template>
                    <template v-slot:item.note="{ value }">
                      <span>{{ value || '—' }}</span>
                    </template>
                    <template v-slot:item.created_at="{ value }">
                      <span>{{ formatDateTime(value) }}</span>
                    </template>
                  </v-data-table>
                </v-card-text>
                </template>
              </v-card>
            </v-window-item>

            <v-window-item value="sellers">
              <v-card flat>
                <v-card-title class="d-flex align-center flex-wrap ga-2">
                  <span>Vendedores</span>
                  <v-spacer></v-spacer>
                  <DialogAssignStoreUser
                    v-if="canMutate"
                    :user="user"
                    :storeId="store.id"
                    v-model="editStoreUserModel"
                    :floatRight="true"
                    @created="onStoreUserSaved"
                    @updated="onStoreUserSaved"
                  />
                </v-card-title>
                <v-card-text>
                  <v-alert
                    v-if="!canMutate"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                  >
                    Selecione um grupo para listar e gerenciar vendedores.
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="sellerHeaders"
                    :items="storeUsers"
                    :loading="loadingSellers"
                    :items-per-page="-1"
                    return-object
                    class="elevation-1"
                    hide-default-footer
                  >
                    <template v-slot:item.user_name="{ item }">
                      <span>{{ sellerName(item) }}</span>
                    </template>
                    <template v-slot:item.user_email="{ item }">
                      <span>{{ sellerEmail(item) }}</span>
                    </template>
                    <template v-slot:item.role="{ value }">
                      <span>{{ roleLabel(value) }}</span>
                    </template>
                    <template v-slot:item.active="{ value }">
                      <span>{{ value ? 'Sim' : 'Não' }}</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-menu>
                        <template v-slot:activator="{ props }">
                          <v-btn icon variant="text" v-bind="props">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="editStoreUserModel = { ...item }">
                            <v-list-item-title>
                              <v-icon color="primary" class="mr-2">mdi-pencil</v-icon>
                              Editar
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="removeStoreUser(item)">
                            <v-list-item-title>
                              <v-icon color="red" class="mr-2">mdi-delete</v-icon>
                              Remover
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-window-item>

            <v-window-item value="sales">
              <v-card flat>
                <v-card-title class="d-flex align-center flex-wrap ga-2">
                  <span>Vendas</span>
                  <v-spacer></v-spacer>
                  <template v-if="canMutate">
                  <DialogIdentifiedSale
                    :user="user"
                    :store="store"
                    :storeProducts="storeProducts"
                    :floatRight="false"
                    @created="onIdentifiedSaleCreated"
                    @paid="onIdentifiedSalePaid"
                    @invoiced="onIdentifiedSaleInvoiced"
                  />
                  <v-select
                    v-model="saleFilters.status"
                    :items="saleStatusFilterItems"
                    item-title="title"
                    item-value="value"
                    label="Status"
                    density="compact"
                    clearable
                    hide-details
                    style="max-width: 240px"
                    @update:model-value="onSaleFilterChange"
                  ></v-select>
                  <v-btn
                    icon
                    variant="text"
                    density="compact"
                    :loading="loadingSales"
                    aria-label="Atualizar vendas"
                    @click="loadSales"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                  </template>
                </v-card-title>
                <v-card-text>
                  <v-alert
                    v-if="!canMutate"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                  >
                    Selecione um grupo para listar vendas desta loja.
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="saleHeaders"
                    :items="sales"
                    :loading="loadingSales"
                    :items-per-page="50"
                    return-object
                    class="elevation-1"
                    hide-default-footer
                  >
                    <template v-slot:item.status="{ value }">
                      <v-chip size="small" variant="tonal" :color="saleStatusColor(value)">
                        {{ saleStatusLabel(value) }}
                      </v-chip>
                    </template>
                    <template v-slot:item.channel="{ value }">
                      <span>{{ saleChannelLabel(value) }}</span>
                    </template>
                    <template v-slot:item.amount_total="{ value }">
                      <span>{{ formatPrice(value) }}</span>
                    </template>
                    <template v-slot:item.tickets_count="{ item }">
                      <span>{{ ticketCount(item) }}</span>
                    </template>
                    <template v-slot:item.invoice_id="{ item }">
                      <span>{{ item.invoice_id || item.invoice?.id || '—' }}</span>
                    </template>
                    <template v-slot:item.paid_at="{ value }">
                      <span>{{ formatDateTime(value) }}</span>
                    </template>
                    <template v-slot:item.identity_mode="{ value }">
                      <span>{{ saleIdentityLabel(value) }}</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        @click="openSaleDetail(item)"
                      >
                        Detalhe
                      </v-btn>
                    </template>
                    <template v-slot:no-data>
                      <div class="text-center text-medium-emphasis py-6">
                        Nenhuma venda ainda
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-window-item>
          </v-window>
        </template>
      </template>
    </v-card>

    <DialogCreateUpdateStore
      :user="user"
      v-model="editStoreModel"
      :hideButton="true"
      @updated="onStoreUpdated"
    />
    <DialogSaleDetail
      v-model="saleDetailModel"
      :canRefundSales="canMutate"
      @refunded="onSaleRefunded"
    />
  </v-container>
</template>
<script>
import DialogAssignStoreUser from '../components/DialogAssignStoreUser.vue'
import DialogAttachStoreProduct from '../components/DialogAttachStoreProduct.vue'
import DialogCreateUpdateStore from '../components/DialogCreateUpdateStore.vue'
import DialogIdentifiedSale from '../components/DialogIdentifiedSale.vue'
import DialogSaleDetail from '../components/DialogSaleDetail.vue'
import DialogStockMove from '../components/DialogStockMove.vue'
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

const STORE_TYPES = [
  { title: 'Normal', value: 'normal' },
  { title: 'Perecíveis', value: 'pereciveis' },
  { title: 'Ingressos', value: 'ingressos' },
]
const STORE_STATUSES = [
  { title: 'Ativa', value: 'active' },
  { title: 'Inativa', value: 'inactive' },
]
const MOVE_TYPES = [
  { title: 'Entrada (alocação)', value: 'in' },
  { title: 'Saída (desalocação)', value: 'out' },
  { title: 'Ajuste', value: 'adjust' },
  { title: 'Venda', value: 'sale' },
  { title: 'Estorno', value: 'refund' },
  { title: 'Reserva', value: 'reserve' },
  { title: 'Libera reserva', value: 'release_reserve' },
  { title: 'Alocação', value: 'allocate' },
  { title: 'Desalocação', value: 'deallocate' },
]
const STORE_USER_ROLES = [
  { title: 'Gerente', value: 'manager' },
  { title: 'Vendedor', value: 'seller' },
  { title: 'Validador de ingresso', value: 'ticket_checker' },
]
const SALE_STATUS_LABELS = {
  open: 'Aberta',
  awaiting_payment: 'Aguardando pagamento',
  paid: 'Paga',
  canceled: 'Cancelada',
  expired: 'Expirada',
  refunded: 'Estornada',
}
const SALE_STATUS_COLORS = {
  open: 'info',
  awaiting_payment: 'warning',
  paid: 'success',
  canceled: 'error',
  expired: 'default',
  refunded: 'secondary',
}
const SALE_CHANNEL_LABELS = {
  pdv: 'PDV',
  web: 'Web',
  admin: 'Admin',
}
const SALE_IDENTITY_LABELS = {
  anonymous: 'Anônima',
  identified: 'Identificada',
}

export default {
  components: {
    DialogAssignStoreUser,
    DialogAttachStoreProduct,
    DialogCreateUpdateStore,
    DialogIdentifiedSale,
    DialogSaleDetail,
    DialogStockMove,
  },
  props: {
    user: Object,
  },
  data() {
    return {
      store: null,
      loadingStore: false,
      loadingProducts: false,
      loadingBalances: false,
      loadingMovements: false,
      loadingSellers: false,
      loadingSales: false,
      // request stamps: ignore stale async results after route/id change
      storeLoadSeq: 0,
      productsLoadSeq: 0,
      balancesLoadSeq: 0,
      movementsLoadSeq: 0,
      sellersLoadSeq: 0,
      salesLoadSeq: 0,
      tab: 'products',
      storeProducts: [],
      balances: [],
      movements: [],
      storeUsers: [],
      sales: [],
      stockLoaded: false,
      sellersLoaded: false,
      salesLoaded: false,
      saleFilters: {
        status: null,
      },
      salePage: 1,
      editStoreModel: {},
      editStoreProductModel: {},
      stockMoveModel: {},
      editStoreUserModel: {},
      saleDetailModel: {},
      productHeaders: [
        { title: 'Produto', align: 'start', key: 'product_name', sortable: true },
        { title: 'Preço base', align: 'end', key: 'base_price', sortable: false },
        { title: 'Preço override', align: 'end', key: 'price_override', sortable: false },
        { title: 'Preço efetivo', align: 'end', key: 'effective_price', sortable: false },
        { title: 'Ativo', align: 'center', key: 'active', sortable: true },
        { title: 'Ações', key: 'actions', align: 'end', sortable: false },
      ],
      balanceHeaders: [
        { title: 'Produto', align: 'start', key: 'product_name', sortable: true },
        { title: 'Variação', align: 'start', key: 'variant_label', sortable: false },
        { title: 'Claim', align: 'end', key: 'quantity', sortable: true },
        { title: 'Disp. loja', align: 'end', key: 'store_available', sortable: false },
        { title: 'Grupo (mão)', align: 'end', key: 'group_on_hand', sortable: false },
        { title: 'Grupo disp.', align: 'end', key: 'group_available', sortable: false },
        { title: 'PDV', align: 'end', key: 'pdv_available', sortable: false },
        { title: 'Mínimo', align: 'end', key: 'min_quantity', sortable: true },
        { title: 'Ações', key: 'actions', align: 'end', sortable: false },
      ],
      movementHeaders: [
        { title: 'Tipo', align: 'start', key: 'type', sortable: true },
        { title: 'Produto', align: 'start', key: 'product_name', sortable: false },
        { title: 'Variação', align: 'start', key: 'variant_label', sortable: false },
        { title: 'Quantidade', align: 'end', key: 'quantity', sortable: true },
        { title: 'Observação', align: 'start', key: 'note', sortable: false },
        { title: 'Data', align: 'end', key: 'created_at', sortable: true },
      ],
      sellerHeaders: [
        { title: 'Nome', align: 'start', key: 'user_name', sortable: true },
        { title: 'E-mail', align: 'start', key: 'user_email', sortable: false },
        { title: 'Função', align: 'start', key: 'role', sortable: true },
        { title: 'Ativo', align: 'center', key: 'active', sortable: true },
        { title: 'Ações', key: 'actions', align: 'end', sortable: false },
      ],
      saleHeaders: [
        { title: 'ID', align: 'start', key: 'id', sortable: true },
        { title: 'Status', align: 'start', key: 'status', sortable: true },
        { title: 'Canal', align: 'start', key: 'channel', sortable: true },
        { title: 'Total', align: 'end', key: 'amount_total', sortable: true },
        { title: 'Ingressos', align: 'end', key: 'tickets_count', sortable: false },
        { title: 'Fatura', align: 'end', key: 'invoice_id', sortable: false },
        { title: 'Pago em', align: 'end', key: 'paid_at', sortable: true },
        { title: 'Identidade', align: 'start', key: 'identity_mode', sortable: true },
        { title: 'Ações', key: 'actions', align: 'end', sortable: false },
      ],
      saleStatusFilterItems: Object.entries(SALE_STATUS_LABELS).map(([value, title]) => ({
        title,
        value,
      })),
    }
  },
  mounted() {
    if (this.canViewStore) {
      this.loadStore()
    }
  },
  activated() {
    if (this.canViewStore) {
      this.loadStore()
    }
  },
  methods: {
    typeLabel(value) {
      const legacy = { permanent: 'Normal', stall: 'Perecíveis', event: 'Ingressos' }
      const found = STORE_TYPES.find((t) => t.value === value)
      return found ? found.title : (legacy[value] || value)
    },
    statusLabel(value) {
      const found = STORE_STATUSES.find((s) => s.value === value)
      return found ? found.title : value
    },
    movementTypeLabel(value) {
      const found = MOVE_TYPES.find((t) => t.value === value)
      return found ? found.title : value
    },
    roleLabel(value) {
      const found = STORE_USER_ROLES.find((r) => r.value === value)
      return found ? found.title : value || '—'
    },
    saleStatusLabel(value) {
      return SALE_STATUS_LABELS[value] || value || '—'
    },
    saleStatusColor(value) {
      return SALE_STATUS_COLORS[value] || 'default'
    },
    saleChannelLabel(value) {
      return SALE_CHANNEL_LABELS[value] || value || '—'
    },
    saleIdentityLabel(value) {
      return SALE_IDENTITY_LABELS[value] || value || '—'
    },
    sellerName(item) {
      return item?.user?.name || item?.user?.legal_person?.name || '—'
    },
    sellerEmail(item) {
      return item?.user?.email || '—'
    },
    ticketCount(item) {
      if (item?.tickets_count != null) return item.tickets_count
      if (Array.isArray(item?.tickets)) return item.tickets.length
      return '—'
    },
    openSaleDetail(item) {
      if (!item?.id) return
      this.saleDetailModel = { id: item.id }
    },
    onSaleRefunded() {
      // Stock restored on BE — refresh sales + balances/movements if stock tab was loaded
      this.loadSales()
      if (this.stockLoaded || this.tab === 'stock') {
        this.loadStockData()
      }
    },
    onIdentifiedSaleCreated() {
      this.loadSales()
    },
    onIdentifiedSalePaid() {
      this.loadSales()
      if (this.stockLoaded || this.tab === 'stock') {
        this.loadStockData()
      }
    },
    onIdentifiedSaleInvoiced() {
      this.loadSales()
    },
    onSaleFilterChange() {
      this.salePage = 1
      this.loadSales()
    },
    goBack() {
      this.$router.push({ name: 'Stores' })
    },
    startEditStore() {
      if (!this.canMutate || !this.store?.id) return
      // clone so form edits do not mutate header state by reference
      this.editStoreModel = { ...this.store }
    },
    productName(item) {
      return item?.product?.name || '—'
    },
    balanceProductName(item) {
      return item?.product?.name || `Produto #${item?.product_id || '—'}`
    },
    variantLabel(item) {
      const v = item?.variant || item?.product_variant
      if (!v) {
        if (item?.variant_label) return item.variant_label
        return '—'
      }
      if (v.name_suffix) return v.name_suffix
      if (v.is_default) return 'Padrão'
      if (Array.isArray(v.options) && v.options.length) {
        return v.options.map((o) => o.value).join(' / ')
      }
      return v.id != null ? `#${v.id}` : '—'
    },
    movementProductName(item) {
      if (item?.product?.name) return item.product.name
      const fromBalance = (this.balances || []).find(
        (b) => b.product_id === item?.product_id || b.product?.id === item?.product_id
      )
      if (fromBalance?.product?.name) return fromBalance.product.name
      const fromSp = (this.storeProducts || []).find(
        (sp) => sp.product_id === item?.product_id || sp.product?.id === item?.product_id
      )
      if (fromSp?.product?.name) return fromSp.product.name
      return item?.product_id != null ? `Produto #${item.product_id}` : '—'
    },
    isLowStock(item) {
      if (!item || item.min_quantity == null) return false
      return Number(item.quantity) <= Number(item.min_quantity)
    },
    balanceRowProps({ item }) {
      if (this.isLowStock(item)) {
        return { class: 'bg-warning-lighten-4 text-warning' }
      }
      return {}
    },
    effectivePrice(item) {
      if (!item) return null
      if (item.price_override !== null && item.price_override !== undefined) {
        return item.price_override
      }
      return item.product?.base_price ?? null
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (isNaN(n)) return value
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatQty(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (isNaN(n)) return value
      return n.toLocaleString('pt-BR')
    },
    formatDateTime(value) {
      if (!value) return '—'
      if (this.$moment) {
        return this.$moment(value).format('DD/MM/YYYY HH:mm')
      }
      try {
        return new Date(value).toLocaleString('pt-BR')
      } catch {
        return value
      }
    },
    startStockMove(item) {
      if (!item) return
      this.stockMoveModel = {
        product_id: item.product_id || item.product?.id,
        product_variant_id: item.product_variant_id || item.variant?.id || null,
        product: item.product || null,
        variant: item.variant || null,
        min_quantity: item.min_quantity,
      }
    },
    loadStore() {
      const vm = this
      const id = vm.$route.params.id
      if (!id || !vm.canViewStore) return
      // always start a new load for current id; stale in-flight results are ignored via seq
      const seq = ++vm.storeLoadSeq
      vm.loadingStore = true
      const query = { withStoreUsers: 0 }
      // cross-group get: include group name when no selected group
      if (vm.isPlatformAdmin && !vm.groupSelected) {
        query.withGroup = 1
      }
      return api.store
        .get(id, query)
        .then((result) => {
          if (seq !== vm.storeLoadSeq || String(vm.$route.params.id) !== String(id)) return
          vm.store = result
          // Nested resources still require selected group (BE canManageGroup)
          if (result?.id && vm.canMutate) {
            const tasks = [vm.loadStoreProducts()]
            if (vm.tab === 'stock') {
              tasks.push(vm.loadStockData())
            }
            if (vm.tab === 'sellers') {
              tasks.push(vm.loadStoreUsers())
            }
            if (vm.tab === 'sales') {
              tasks.push(vm.loadSales())
            }
            return Promise.all(tasks)
          }
        })
        .catch((err) => {
          if (seq !== vm.storeLoadSeq) return
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          if (seq === vm.storeLoadSeq) {
            vm.loadingStore = false
          }
        })
    },
    loadStoreProducts() {
      const vm = this
      const storeId = vm.store?.id
      if (!storeId || !vm.canMutate) return
      const seq = ++vm.productsLoadSeq
      vm.loadingProducts = true
      return api.storeProduct
        .list({ store_id: storeId, itemsPerPage: 200 })
        .then((result) => {
          if (seq !== vm.productsLoadSeq || vm.store?.id !== storeId) return
          const { items } = normalizeListPayload(result)
          vm.storeProducts = items
        })
        .catch((err) => {
          if (seq !== vm.productsLoadSeq) return
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          if (seq === vm.productsLoadSeq) {
            vm.loadingProducts = false
          }
        })
    },
    loadStockData() {
      const storeId = this.store?.id
      return Promise.all([this.loadBalances(), this.loadMovements()]).then((r) => {
        if (this.store?.id === storeId) {
          this.stockLoaded = true
        }
        return r
      })
    },
    loadBalances() {
      const vm = this
      const storeId = vm.store?.id
      if (!storeId || !vm.canMutate) return Promise.resolve()
      const seq = ++vm.balancesLoadSeq
      vm.loadingBalances = true
      return api.stock
        .listBalances({ store_id: storeId, itemsPerPage: 200 })
        .then((result) => {
          if (seq !== vm.balancesLoadSeq || vm.store?.id !== storeId) return
          const { items } = normalizeListPayload(result)
          vm.balances = items
        })
        .catch((err) => {
          if (seq !== vm.balancesLoadSeq) return
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          if (seq === vm.balancesLoadSeq) {
            vm.loadingBalances = false
          }
        })
    },
    loadMovements() {
      const vm = this
      const storeId = vm.store?.id
      if (!storeId || !vm.canMutate) return Promise.resolve()
      const seq = ++vm.movementsLoadSeq
      vm.loadingMovements = true
      return api.stock
        .listMovements({ store_id: storeId, itemsPerPage: 50 })
        .then((result) => {
          if (seq !== vm.movementsLoadSeq || vm.store?.id !== storeId) return
          const { items } = normalizeListPayload(result)
          vm.movements = items
        })
        .catch((err) => {
          if (seq !== vm.movementsLoadSeq) return
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          if (seq === vm.movementsLoadSeq) {
            vm.loadingMovements = false
          }
        })
    },
    onStoreUpdated(val) {
      const vm = this
      vm.editStoreModel = {}
      if (val?.id) {
        vm.store = { ...vm.store, ...val }
      } else {
        vm.loadStore()
      }
    },
    onStoreProductSaved() {
      const vm = this
      vm.editStoreProductModel = {}
      // refresh list (response may be { store_product, stock_balance })
      vm.loadStoreProducts()
      // Task 6 polish: refresh balances after attach (initial_quantity may create stock)
      if (vm.store?.id) {
        vm.loadBalances()
        if (vm.stockLoaded || vm.tab === 'stock') {
          vm.loadMovements()
        }
      }
    },
    onStockMoved() {
      this.stockMoveModel = {}
      this.loadBalances()
      this.loadMovements()
    },
    loadStoreUsers() {
      const vm = this
      const storeId = vm.store?.id
      if (!storeId || !vm.canMutate) return Promise.resolve()
      const seq = ++vm.sellersLoadSeq
      vm.loadingSellers = true
      return api.storeUser
        .list({ store_id: storeId, itemsPerPage: 200 })
        .then((result) => {
          if (seq !== vm.sellersLoadSeq || vm.store?.id !== storeId) return
          const { items } = normalizeListPayload(result)
          vm.storeUsers = items
          vm.sellersLoaded = true
        })
        .catch((err) => {
          if (seq !== vm.sellersLoadSeq) return
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          if (seq === vm.sellersLoadSeq) {
            vm.loadingSellers = false
          }
        })
    },
    removeStoreUser(item) {
      const vm = this
      if (!vm.canMutate || !item?.id) return
      const name = vm.sellerName(item)
      const ok = confirm(`Deseja realmente remover ${name} desta loja?`)
      if (!ok) return
      return api.storeUser
        .delete(item.id)
        .then(() => {
          vm.$notify({ type: 'success', text: 'Vendedor removido' })
          vm.loadStoreUsers()
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
    },
    onStoreUserSaved() {
      this.editStoreUserModel = {}
      this.loadStoreUsers()
    },
    loadSales() {
      const vm = this
      const storeId = vm.store?.id
      if (!storeId || !vm.canMutate) return Promise.resolve()
      const seq = ++vm.salesLoadSeq
      vm.loadingSales = true
      const payload = {
        store_id: Number(storeId),
        itemsPerPage: 50,
      }
      if (vm.saleFilters?.status) {
        payload.status = vm.saleFilters.status
      }
      return api.sale
        .list(payload, vm.salePage || 1)
        .then((result) => {
          if (seq !== vm.salesLoadSeq || vm.store?.id !== storeId) return
          const { items } = normalizeListPayload(result)
          vm.sales = items
          vm.salesLoaded = true
        })
        .catch((err) => {
          if (seq !== vm.salesLoadSeq) return
          // empty list is not an error; keep prior items only on real failures
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          if (seq === vm.salesLoadSeq) {
            vm.loadingSales = false
          }
        })
    },
  },
  computed: {
    groupSelected() {
      return api.storage.isGroupSelected()
    },
    isPlatformAdmin() {
      return (
        this.user?.access_level >= this.$USER_ACCESS_LEVELS_DIC.ADMIN.value
      )
    },
    /** Header/detail GET: group selected or platform admin cross-group */
    canViewStore() {
      return this.groupSelected || this.isPlatformAdmin
    },
    /** Mutations + nested lists still require selected group (BE canManageGroup) */
    canMutate() {
      return this.groupSelected
    },
    /** Already linked catalog product ids — hide from attach picker on create */
    attachedProductIds() {
      return (this.storeProducts || [])
        .map((sp) => sp.product_id || sp.product?.id)
        .filter((id) => id != null)
    },
    stockProductOptions() {
      // balances first (have product + qty context), then store products
      return [...(this.balances || []), ...(this.storeProducts || [])]
    },
  },
  watch: {
    '$route.params.id'(newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.canViewStore) {
        // invalidate all in-flight loaders for previous store
        this.storeLoadSeq++
        this.productsLoadSeq++
        this.balancesLoadSeq++
        this.movementsLoadSeq++
        this.sellersLoadSeq++
        this.salesLoadSeq++
        this.store = null
        this.storeProducts = []
        this.balances = []
        this.movements = []
        this.storeUsers = []
        this.sales = []
        this.stockLoaded = false
        this.sellersLoaded = false
        this.salesLoaded = false
        this.saleFilters = { status: null }
        this.salePage = 1
        this.saleDetailModel = {}
        this.tab = 'products'
        this.loadingStore = false
        this.loadingProducts = false
        this.loadingBalances = false
        this.loadingMovements = false
        this.loadingSellers = false
        this.loadingSales = false
        this.loadStore()
      }
    },
    tab(newVal) {
      if (!this.canMutate || !this.store?.id) return
      if (newVal === 'stock') {
        this.loadStockData()
      }
      if (newVal === 'sellers') {
        this.loadStoreUsers()
      }
      if (newVal === 'sales') {
        this.loadSales()
      }
    },
  },
}
</script>
